import WarpElement from '@warp-ds/elements-core';
import { css } from 'lit';
import { html } from 'lit/static-html.js';
import pulse from '@finn-no/pulse-sdk';
import './market-icon.js';
import '@warp-ds/components/icon';

const safe = (val) => val || '';
const replaceSpacesWithDashes = (value) => safe(value).replace(/\s+/g, '-');

export class MarketItem extends WarpElement {
    static styles = [
        ...WarpElement.styles,
        css`
            .two-lines {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.focus\:\[--w-outline-offset\:-2px\]:focus{--w-outline-offset:-2px}.no-underline,.hover\:no-underline:hover,.focus\:no-underline:focus,.active\:no-underline:active{text-decoration:none}.focus\:focusable:focus{outline:2px solid var(--w-s-color-border-focus);outline-offset:var(--w-outline-offset,1px)}.self-stretch{align-self:stretch}.order-last{order:9999}.static{position:static}.s-text-static{color:var(--w-s-color-text-static)}.hover\:s-text-subtle:hover{color:var(--w-s-color-text-subtle)}.s-icon-subtle{color:var(--w-s-color-icon-subtle)}.w-auto{width:auto}.px-4{padding-left:.4rem;padding-right:.4rem}.hyphens-auto{hyphens:auto}.text-ellipsis{text-overflow:ellipsis}@media (width>=480px){.sm\:text-s{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}};
        `,
    ];

    static properties = {
        market: { type: Object, attribute: 'market', reflect: true },
        index: { type: Number, attribute: 'index', reflect: true },
        locale: { type: String, attribute: 'locale', reflect: true },
        brand: { type: String, attribute: 'brand', reflect: true },
        isHovered: { type: Boolean, state: true },
    };

    trackMarketLinkClick(trackingData) {
        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: trackingData.name,
            object: {
                id: trackingData.object.id,
                type: 'UIElement',
                elementType: trackingData.object.elementType,
                position: trackingData.object.position,
            },
            target: trackingData.target,
            vertical: {
                '@type': trackingData.vertical.type,
                name: trackingData.vertical.name,
            },
        });
    }

    connectedCallback() {
        super.connectedCallback();
        this.isHovered = false;
    }

    get isLogo() {
        return this.market?.icon?.icon_type === "logo";
    }

    _handleMouseEnter() {
        this.isHovered = true;
    }

    _handleMouseLeave() {
        this.isHovered = false;
    }

    render() {
        if (!this.market) {
            return html`<div></div>`;
        }

        const { label, destinations, tracking, is_external } = this.market;
        const index = this.index || 0;

        return html`<div class="market-grid__item text-center relative w-auto" id="parentElement">
            <a
                class="group px-4 py-8 overflow-hidden text-ellipsis text-xs sm:text-s flex flex-col items-center no-underline hover:no-underline active:no-underline focus:no-underline focus:focusable focus:[--w-outline-offset:-2px]"
                href=${safe(destinations.url) || safe(destinations.path)}
                data-icon-name=${replaceSpacesWithDashes(label)}
                @click=${() => this.trackMarketLinkClick(tracking)}
                @mouseenter=${this._handleMouseEnter}
                @mouseleave=${this._handleMouseLeave}
                ?data-logo=${this.isLogo}
                target=${is_external ? '"_blank"' : null}
                id=${index === 0 ? 'first-market-link' : null}
            >
                <span class="order-last mt-4 s-text-static self-stretch text-s overflow-hidden two-lines hyphens-auto hover:s-text-subtle">${safe(label)}</span>
                <span class="sr-only">,&nbsp;</span>
                <span class="sr-only">
                  ${
                      is_external
                          ? this.i18n?.t({ id: 'frontpage.market-icon.internal.description.primary', message: 'Primary icon of' })
                          : this.i18n?.t({ id: 'frontpage.market-icon.internal.description', message: 'Icon of' })
                  }
                </span>
                <market-icon 
                    .market=${this.market} 
                    .locale=${this.locale} 
                    .brand=${this.brand}
                    .isHovered=${this.isHovered}
                ></market-icon>
         ${
             is_external
                 ? html` <span class="sr-only">,&nbsp;
                           ${this.i18n?.t({
                               id: 'frontpage.market-icon.internal.description.secondary',
                               message: 'Secondary icon of',
                           })}
                       </span>
                 <w-icon name="LinkExternal" size="small" locale="${this.locale}" class="absolute right-8 top-8 pointer-events-none s-icon-subtle"></w-icon>`
                 : ''
         } 
            </a>
        </div>`;
    }
}

if (!customElements.get('market-item')) {
    customElements.define('market-item', MarketItem);
}
